import React from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { ValueOf } from 'type-fest';
import materialIcons from '../icon/constants/material-icons-constants';
import Icon from '../../components/icon';

// style
import { ButtonProps } from '../button/button';
import { sizes } from '../icon/style';
import { useTagContext } from './context';
import { TagProps } from './tag';
import {
  ActionIconButtonVariants,
  getColor,
  getHoverBackgroundColor,
  getActiveBackgroundColor,
  getDisabledBackgroundColor,
  getBackgroundColor,
  getDisabledColor,
  getDisabledOpacity,
} from './tag-action-icon-style';

interface ActionIconProps extends Omit<ButtonProps, 'size' | 'variant'> {
  placement?: 'right' | 'left' | 'static';
  icon?: keyof typeof materialIcons;
  variant?: ActionIconButtonVariants;
  size?: TagProps['size'];
}

const actionIconSize: Record<
  NonNullable<TagProps['size']>,
  { size: ValueOf<typeof sizes>; iconSize: keyof typeof sizes }
> = {
  xxs: { size: sizes['xxs'], iconSize: 'xxs' },
  xs: { size: sizes['xs'], iconSize: 'xs' },
  sm: { size: sizes['lg'], iconSize: 'md' },
  md: { size: sizes['xxl'], iconSize: 'md' },
  lg: { size: sizes['xxl'], iconSize: 'md' },
  xl: { size: sizes['xxl'], iconSize: 'md' },
  xxl: { size: sizes['xxl'], iconSize: 'md' },
  auto: { size: sizes['xxl'], iconSize: 'md' },
};

const ActionIconComponent = function ({
  children,
  onClick,
  placement = 'right',
  icon = 'CLOSE',
  className,
  disabled,
  size = 'md',
  ...rest
}: ActionIconProps) {
  const { iconSize } = actionIconSize[size];

  return (
    <button
      disabled={disabled}
      aria-label={icon}
      type={'button'}
      className={classNames('box-tag-action-icon', `placement-${placement}`, className)}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(e);
      }}
      {...rest}
    >
      {children || <Icon size={iconSize} name={icon} variant={'inherit'} />}
    </button>
  );
};

const StyledActionIcon = styled(ActionIconComponent)<ActionIconProps>`
  flex-shrink: 0;
  border: none;
  ${({ variant = 'inherit', theme }) => css`
    background: ${getBackgroundColor({ variant, actionColor: theme.actionColor })};
  `}
  ${({ size = 'md' }) => css`
    width: ${actionIconSize[size].size};
    height: ${actionIconSize[size].size};
  `}
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: 0.2s;
  ${({ variant = 'inherit' }) => css`
    color: ${getColor({ variant })};
  `}

  &:hover:not([disabled]) {
    ${({ variant = 'inherit', theme }) => css`
      background: ${getHoverBackgroundColor({ variant, actionColorDark: theme.actionColorDark })};
    `}
    cursor: pointer;
  }

  &:active:not([disabled]),
  &:active:hover:not([disabled]) {
    ${({ variant = 'inherit', theme }) => css`
      background: ${getActiveBackgroundColor({
        variant,
        actionColorDark: theme.actionColorDark,
        actionLight: theme.actionLight,
      })};
    `}
  }

  &[disabled] {
    cursor: not-allowed;
    color: ${({ variant = 'inherit' }) => getDisabledColor({ variant })};
    background: ${({ variant = 'inherit' }) => getDisabledBackgroundColor({ variant })};
    opacity: ${({ variant = 'inherit' }) => getDisabledOpacity({ variant })};
  }

  &.placement-right {
    margin-left: 16px;
  }

  &.placement-left {
    margin-right: 16px;
  }
`;

const ActionIcon: (props: ActionIconProps) => React.JSX.Element = ({ ...rest }) => {
  const { size } = useTagContext();

  return <StyledActionIcon size={size} {...rest} />;
};

export { ActionIcon };
